import { atomWithStorage } from '@teetsh/app/src/utils/atomWithStorage';
import { CJFilterStatus } from '@teetsh/app/src/types/event';
import { useAtom, useAtomValue } from 'jotai';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useEffect } from 'react';
import { CahierJournalView } from '@teetsh/app/src/containers/CahierJournal/types';

// On utilise ici un fork, sinon on se tape un unexpected syntax on recette pour chrome 65
// surement un problème de compil, une fois passé sur vite ça pourra valoir le coup de retester

interface SchoolSettings {
  classroomsId: string[];
  allClassroomsIds: string;
}

export type Density = 'normal' | 'compact';

interface CJDisplayConfiguration {
  expanded: boolean;
  view: CahierJournalView;
  status: CJFilterStatus;
  schoolSettings: { [key: string]: SchoolSettings };
  globalScroll?: boolean;
  density?: Density;
  hideBirthdays?: boolean;
  hideEvents?: boolean;
  quickDayDownload?: boolean;
  quickWeekDownload?: boolean;
  aiPaused?: boolean;
}

const configurationDisplayAtom = atomWithStorage<CJDisplayConfiguration>(
  'cjDisplayConfiguration',
  {
    expanded: true,
    view: 'week',
    status: 'all',
    globalScroll: false,
    density: 'normal',
    hideBirthdays: false,
    hideEvents: false,
    schoolSettings: {},
    quickDayDownload: false,
    quickWeekDownload: false,
    aiPaused: false,
  },
);
export const useConfigurationDisplayValue = () =>
  useAtomValue(configurationDisplayAtom);

function getSchoolSettings(
  s: CJDisplayConfiguration,
  schoolId: string,
): SchoolSettings | undefined {
  if (s?.schoolSettings && s?.schoolSettings[schoolId]) {
    return s.schoolSettings[schoolId];
  }
  return undefined;
}

export function useCJConfigurationDisplay() {
  const { school } = useSchoolSchoolyear();
  const allClassrooms = useClassrooms();
  const [configurationDisplay, setConfigurationDisplay] = useAtom(
    configurationDisplayAtom,
  );
  useEffect(() => {
    const allClassroomsIds = allClassrooms.map(c => c.id).join('');
    const schoolSettings = getSchoolSettings(configurationDisplay, school.id);
    // Refresh school settings if classrooms have changed
    if (schoolSettings?.allClassroomsIds !== allClassroomsIds) {
      setConfigurationDisplay({
        ...configurationDisplay,
        schoolSettings: {
          ...configurationDisplay.schoolSettings,
          [school.id]: {
            ...(getSchoolSettings(configurationDisplay, school.id) || {}),
            allClassroomsIds,
            classroomsId: allClassrooms.map(c => c.id),
          },
        },
      });
    }
  }, [allClassrooms.map(c => c.id).join('')]);
  const schoolSettings = getSchoolSettings(configurationDisplay, school.id);
  return {
    expanded: configurationDisplay.expanded,
    view: configurationDisplay.view,
    classroomsId: schoolSettings?.classroomsId || allClassrooms.map(c => c.id),
    status: configurationDisplay.status || 'all',
    hideEvents: configurationDisplay?.hideEvents || false,
    hideBirthdays: configurationDisplay?.hideBirthdays || false,
    globalScroll: configurationDisplay?.globalScroll || false,
    density: configurationDisplay?.density || 'normal',
    quickWeekDownload: configurationDisplay?.quickWeekDownload || false,
    quickDayDownload: configurationDisplay?.quickDayDownload || false,
    aiPaused: configurationDisplay?.aiPaused || false,
    setQuickWeekDownload: (quickWeekDownload: boolean) =>
      setConfigurationDisplay({
        ...configurationDisplay,
        quickWeekDownload,
      }),
    setQuickDayDownload: (quickDayDownload: boolean) =>
      setConfigurationDisplay({
        ...configurationDisplay,
        quickDayDownload,
      }),
    setHideEvents: (hideEvents: boolean) =>
      setConfigurationDisplay({ ...configurationDisplay, hideEvents }),
    setHideBirthdays: (hideBirthdays: boolean) =>
      setConfigurationDisplay({ ...configurationDisplay, hideBirthdays }),
    setItemsExpanded: (expanded: boolean) =>
      setConfigurationDisplay({ ...configurationDisplay, expanded }),
    setView: (view: CahierJournalView) =>
      setConfigurationDisplay({ ...configurationDisplay, view }),
    setClassroomsId: (classroomsId: string[]) =>
      setConfigurationDisplay({
        ...configurationDisplay,
        schoolSettings: {
          ...configurationDisplay.schoolSettings,
          [school.id]: {
            ...(configurationDisplay.schoolSettings[school.id] || {}),
            classroomsId,
          },
        },
      }),
    setStatus: (status: CJFilterStatus) =>
      setConfigurationDisplay({ ...configurationDisplay, status }),
    setGlobalScroll: (globalScroll: boolean) =>
      setConfigurationDisplay({ ...configurationDisplay, globalScroll }),
    setDensity: (density: 'normal' | 'compact') =>
      setConfigurationDisplay({ ...configurationDisplay, density }),
    setAIPaused: (aiPaused: boolean) =>
      setConfigurationDisplay({ ...configurationDisplay, aiPaused }),
  };
}
